// Importing necessary libraries and components
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, TextField, Checkbox } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import styles from "assets/jss/material-dashboard-react/components/businessForm";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format } from "date-fns";
import { createTheme } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import {
  getProviderByFilterData,
  addSearchData,
} from "../../Redux/Actions/ProvideRegistration";
import {
  getTransactionHistoryData,
  setFormDataTransactionHistory,
  clearFormDataTransactionHistory,
} from "../../Redux/Actions/merchantInformation";

// Custom theme for Material-UI components
export const customTheme = createTheme({
  palette: {
    primary: {
      main: "#003c6e", // Primary color
      light: "#003c6e",
      dark: "#003c6e",
    },
    secondary: {
      main: "#003c6e", // Secondary color
    },
  },
  overrides: {
    MuiPickersBasePicker: {
      container: {
        width: "300px",
        height: "370px",
      },
      pickerView: {
        width: "280px",
        height: "280px",
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        fontSize: "0.9rem",
      },
      dayLabel: {
        fontSize: "0.7rem",
      },
    },
    MuiPickersDay: {
      day: {
        fontSize: "0.8rem",
      },
    },
    MuiTypography: {
      h4: {
        fontSize: "1rem",
      },
    },
  },
});

// Custom styles for the component
const useStyles = makeStyles(styles);
const searchButtonStyles = makeStyles(theme => ({
  searchButton: {
    backgroundColor: theme.palette.primary.main, // Button background color
    "&:hover": {
      backgroundColor: theme.palette.primary.main, // Hover effect
      boxShadow:
        "0 14px 26px -12px rgb(153 153 153 / 42%), 0 4px 23px 0px rgb(0 0 0 / 12%), 0 8px 10px -5px rgb(153 153 153 / 20%)",
    },
  },
}));

// Main component for Search Refund Dialog
function SearchRefund(props) {
  const classes = useStyles();
  const [showAmountRange, setShowAmountRange] = React.useState(false); // Toggle for amount range
  const [showDateRange, setShowDateRange] = React.useState(false); // Toggle for date range
  const searchButtonClasses = searchButtonStyles();
  const profileId = localStorage.getItem("profileId");
  const [txn1, setTxn1] = React.useState(""); // Transaction ID
  const [amount1, setAmount1] = React.useState(""); // Start amount
  const [amount2, setAmount2] = React.useState(""); // End amount
  const [date1, setDate1] = React.useState(""); // Start date
  const [date2, setDate2] = React.useState(""); // End date
  const [id, setId] = React.useState(null); // ID from URL query parameters

  // Effect to initialize amount and date range toggles from localStorage
  React.useEffect(() => {
    const savedIsAmountRangeChecked = localStorage.getItem("isAmountRangeChecked");
    setShowAmountRange(savedIsAmountRangeChecked === "true");
    const savedIsDateRangeChecked = localStorage.getItem("isDateRangeChecked");
    setShowDateRange(savedIsDateRangeChecked === "true");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to fetch ID from URL query parameters
  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const idFromUrl = queryParams.get("id");
    setId(idFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to clear form data
  const clearFormData = () => {
    const initialFormData = {
      searchTxnID: "",
      searchStartAmount: "",
      searchEndAmount: "",
      searchStartDate: "",
      searchEndDate: "",
      dateRange: false,
      amountRange: false,
    };
    setShowAmountRange(false);
    setShowDateRange(false);
    localStorage.removeItem("isAmountRangeChecked");
    localStorage.removeItem("isDateRangeChecked");
    props.setFormDataTransactionHistory(initialFormData);
  };

  // Function to handle amount range toggle
  const handleAmountCheckboxChange = event => {
    setShowAmountRange(event.target.checked);
    if (event.target.checked) {
      setAmount1("");
      setShowAmountRange(true);
    } else {
      setShowAmountRange(false);
      setAmount1("");
      setAmount2("");
    }
  };

  // Function to handle date range toggle
  const handleDateCheckboxChange = event => {
    setShowDateRange(event.target.checked);
    if (event.target.checked) {
      setDate1("");
      setShowDateRange(true);
    } else {
      setDate1("");
      setDate2("");
      setShowDateRange(false);
    }
  };

  const handleFromDateChange = date => {
    let formattedDate = null;
    if (date instanceof Date) {
      formattedDate = format(date, "yyyy-MM-dd");
    } else if (typeof date === "string") {
      const parts = date.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    setDate1(formattedDate);
  };

  const handleToDateChange = date => {
    let formattedDate = null;
    if (date instanceof Date) {
      formattedDate = format(date, "yyyy-MM-dd");
    } else if (typeof date === "string") {
      const parts = date.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    setDate2(formattedDate);
  };

  // Function to handle dialog close
  const handleClose = () => {
    if (id) {
      const payload = {
        Bussines_id: id,
        recordPerPage: 5,
        pageNo: 1,
      };
      props.getTransactionHistoryData(payload);
    } else {
      const payload = {
        Profile_id: profileId,
        recordPerPage: 5,
        pageNo: 1,
      };
      props.getTransactionHistoryData(payload);
    }
    props.clearFormDataTransactionHistory();
    clearFormData();
    props.clearChips();
  };

  // Function to handle form submission
  const onFormSubmit = event => {
    event.preventDefault();
    const submitFormData = {
      searchTxnID: txn1.trim(),
      searchStartAmount: amount1.trim(),
      searchEndAmount: amount2.trim(),
      searchStartDate: date1,
      searchEndDate: date2,
      amountRange: showAmountRange,
      dateRange: showDateRange,
    };
    if (showAmountRange === true) {
      localStorage.setItem("isAmountRangeChecked", true);
    } else {
      localStorage.removeItem("isAmountRangeChecked");
    }

    if (showDateRange === true) {
      localStorage.setItem("isDateRangeChecked", true);
    } else {
      localStorage.removeItem("isDateRangeChecked");
    }

    props.setFormDataTransactionHistory(submitFormData);
    if (id) {
      const payload = {
        Bussines_id: id,
        recordPerPage: props?.formData?.recordPerPage,
        searchTxnID: txn1.trim(),
        ...(showAmountRange
          ? {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }
          : {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }),
        ...(showDateRange
          ? {
            searchStartDate: date1,
            searchEndDate: date2,
          }
          : {
            searchStartDate: date1,
            searchEndDate: date2,
          }),
        amountRange: showAmountRange,
        dateRange: showDateRange,
        pageNo: 1,
      };
      props.getTransactionHistoryData(payload);
      props.seacrhChipData(payload);
      props.close();
    } else {
      const payload = {
        Profile_id: profileId,
        recordPerPage: props?.formData?.recordPerPage,
        searchTxnID: txn1.trim(),
        ...(showAmountRange
          ? {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }
          : {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }),
        ...(showDateRange
          ? {
            searchStartDate: date1,
            searchEndDate: date2,
          }
          : {
            searchStartDate: date1,
            searchEndDate: date2,
          }),
        amountRange: showAmountRange,
        dateRange: showDateRange,
        pageNo: 1,
      };
      props.getTransactionHistoryData(payload);
      props.seacrhChipData(payload);
      props.close();
    }
  };

  // Effect to populate form fields from Redux state
  React.useEffect(() => {
    if (props.formData.searchTxnID) {
      setTxn1(props.formData.searchTxnID);
    }
    if (props.formData.searchStartAmount) {
      setAmount1(props.formData.searchStartAmount);
    }
    if (props.formData.searchEndAmount) {
      setAmount2(props.formData.searchEndAmount);
    }
    if (props.formData.searchStartDate) {
      setDate1(props.formData.searchStartDate);
    }
    if (props.formData.searchEndDate) {
      setDate2(props.formData.searchEndDate);
    }
  }, [props.formData]);

  const handleInputChange1 = e => {
    setTxn1(e.target.value);
  };

  const handleInputChange2 = e => {
    setAmount1(e.target.value);
  };

  return (
    <MuiThemeProvider theme={customTheme}>
      <form onSubmit={e => onFormSubmit(e)}>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label="Ref Txn ID"
              value={txn1}
              onChange={handleInputChange1}
              name="searchTxnID"
              id="txnID"
              style={{ fontSize: "82%" }}
            />
          </Grid>
        </Grid>

        <Grid
          container
          className={classes.margin}
        >
          <Grid item xs container alignItems="center">
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLabelColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label={showAmountRange ? "From" : "Amount ($)"}
              value={amount1}
              onChange={handleInputChange2}
              name="searchStartAmount"
              id="searchStartAmount"
              style={{
                fontSize: "82%",
                width: showAmountRange ? "50%" : "100%",
              }}
            />
            {showAmountRange && (
              <TextField
                className={classes.rootDialog}
                label="To"
                value={amount2}
                onChange={e => setAmount2(e.target.value)}
                name="searchEndAmount"
                id="searchEndAmount"
                style={{ fontSize: "82%", width: "50%" }}
              />
            )}
            <Checkbox
              checked={showAmountRange}
              onChange={handleAmountCheckboxChange}
              color="primary"
              style={{ marginLeft: "auto" }}
            />
            <label style={{ fontSize: "12px", marginBottom: 0 }}>
              {showAmountRange ? "Amount ($)" : "Search In Range"}
            </label>
          </Grid>
        </Grid>

        {/* Date Range Inputs */}
        <Grid container className={classes.margin}>
          <Grid item xs container alignItems="center">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                autoOk
                variant="inline"
                label={showDateRange ? "From" : "Date"}
                value={date1 ? new Date(date1) : null}
                onChange={handleFromDateChange}
                format="yyyy/MM/dd"
                maxDate={new Date()}
                style={{
                  width: showDateRange ? "50%" : "100%",
                }}
              />

              {inputProps => (
                <TextField
                  {...inputProps}
                  className={classes.rootDialog}
                  InputLabelProps={{
                    classes: {
                      root: classes.activeInputLabelColor,
                      focused: "focused",
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.activeInputColor,
                      focused: "focused",
                      input: classes.resize,
                    },
                  }}
                  label={showDateRange ? "From" : "Date"}
                  style={{
                    width: showDateRange ? "50%" : "100%",
                    fontSize: "100%",
                  }}
                />
              )}
            </MuiPickersUtilsProvider>
            {showDateRange && (
              <>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    variant="inline"
                    label="To"
                    value={date2 ? new Date(date2) : null}
                    onChange={handleToDateChange}
                    format="yyyy/MM/dd"
                    maxDate={new Date()}
                    style={{
                      width: "50%",
                    }}
                  />
                  {inputProps => (
                    <TextField
                      {...inputProps}
                      className={classes.rootDialog}
                      InputLabelProps={{
                        classes: {
                          root: classes.activeInputLabelColor,
                          focused: "focused",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.activeInputColor,
                          focused: "focused",
                          input: classes.resize,
                        },
                      }}
                      label="To"
                      style={{ width: "50%", fontSize: "82%" }}
                    />
                  )}
                </MuiPickersUtilsProvider>
              </>
            )}
            <Checkbox
              checked={showDateRange}
              onChange={handleDateCheckboxChange}
              color="primary"
              style={{ marginLeft: "auto" }}
            />
            <label style={{ fontSize: "12px", marginBottom: 0 }}>
              {showDateRange ? "Date" : "Search In Range"}
            </label>
          </Grid>
        </Grid>
        <Grid
          container
          className={classes.margin}
          style={{ justifyContent: "end" }}
        >
          <Button
            variant="contained"
            className={classes.clearButton}
            disabled={
              txn1 === ""
              && amount1 === ""
              && amount2 === ""
              && date1 === ""
              && date2 === ""
            }
            onClick={() => handleClose()}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`${classes.buttonStyle} ${searchButtonClasses.searchButton}`}
            disabled={
              txn1 === ""
              && amount1 === ""
              && amount2 === ""
              && date1 === ""
              && date2 === ""
            }
            type="submit"
          >
            Search
          </Button>
        </Grid>
      </form>
    </MuiThemeProvider>
  );
}

// Prop types for the component
SearchRefund.propTypes = {
  close: PropTypes.func,
  tableData: PropTypes.object,
  getProviderByFilterData: PropTypes.func,
  seacrhChipData: PropTypes.func,
  addSearchData: PropTypes.func,
  getTransactionHistoryData: PropTypes.func,
};

// Mapping Redux state to props
const mapStateToProps = app => ({
  formData: app.merchantInformation,
  tableData: app.provideRegistration,
  userDetails: app.app,
  merchantData: app.merchantInformation,
  transactionData: app.merchantInformation,
});

// Connecting the component to Redux
export default connect(mapStateToProps, {
  setFormDataTransactionHistory,
  clearFormDataTransactionHistory,
  getTransactionHistoryData,
  getProviderByFilterData,
  addSearchData,
})(SearchRefund);
